import {createFeatureSelector, createSelector} from '@ngrx/store';
import {generateIndependentSelectorCreatorsFor, generateLoadStatusSelectorsFor} from '@aviary/store/store.selector-generators';
import {MxSelector, MxSelectorCreator, NO_NAME_PREFIX} from '@aviary/store/store.types';
import {
  AMOUNT_OF_VISIBLE_NOTIFICATIONS,
  Notification,
  NOTIFICATION_ICON_CONFIG,
  NOTIFICATION_TYPE_CONFIG,
  NotificationIcon,
  NOTIFICATIONS_FEATURE_KEY,
  NotificationTypeDetails
} from '@store/notifications/notifications.types';
import {NotificationsState} from './notifications.state';
import {FALLBACK_NOTIFICATION, FALLBACK_NOTIFICATION_ICON} from '@store/notifications/notifications.fallback';
import {isPopulatedArray, isPositiveNumber} from '@aviary/store/common/typing.helpers';

const entireState: MxSelector<NotificationsState> = createFeatureSelector(NOTIFICATIONS_FEATURE_KEY);

export namespace NotificationsSelectors {
  export namespace Stateless {
    const independentSelectorCreators = generateIndependentSelectorCreatorsFor(FALLBACK_NOTIFICATION, NO_NAME_PREFIX);

    export const {
      idFor,
      patronIdFor,
      patronNotificationTypeFor,
      subjectFor,
      messageFor,
      emailSentFor,
      smsSentFor,
      activeFor,
      viewedFor,
      createdFor,
      modifiedFor,
    } = independentSelectorCreators;

    export const displayableTypeLabelFor: MxSelectorCreator<[Notification], string> =
      _notification => createSelector(
        patronNotificationTypeFor(_notification),
        _type => {
          const typeConfig: NotificationTypeDetails = NOTIFICATION_TYPE_CONFIG[_type];
          return typeConfig ? typeConfig.label : 'Unknown notification';
        }
      );

    export const iconNameFor: MxSelectorCreator<[Notification], string> =
      _notification => createSelector(
        patronNotificationTypeFor(_notification),
        _type => {
          const typeConfig: NotificationTypeDetails = NOTIFICATION_TYPE_CONFIG[_type];
          const iconConfig: NotificationIcon = typeConfig ? NOTIFICATION_ICON_CONFIG[typeConfig.icon] : FALLBACK_NOTIFICATION_ICON;
          return iconConfig.iconName as string;
        }
      );

    export const iconClassesFor: MxSelectorCreator<[Notification], string> =
      _notification => createSelector(
        patronNotificationTypeFor(_notification),
        _type => {
          const typeConfig: NotificationTypeDetails = NOTIFICATION_TYPE_CONFIG[_type];
          const iconConfig: NotificationIcon = typeConfig ? NOTIFICATION_ICON_CONFIG[typeConfig.icon] : FALLBACK_NOTIFICATION_ICON;
          return iconConfig.iconClasses;
        }
      );
  } // namespace Stateless

  export namespace Notifications {
    const subState: MxSelector<NotificationsState['notifications']> = createSelector(
      entireState,
      state => state.notifications
    );

    // LOAD STATUS
    export const {
      loading,
      awaitingFirstLoad,
      loaded,
      finishedLoading
    } = generateLoadStatusSelectorsFor(subState);


    export const unread: MxSelector<Notification[]> = createSelector(
      subState,
      _subState => _subState.unread ?? []
    );

    export const hasUnreadNotifications: MxSelector<boolean> = createSelector(
      unread,
      _unread => isPopulatedArray(_unread)
    );

    export const noUnreadNotifications: MxSelector<boolean> = createSelector(
      hasUnreadNotifications,
      _hasUnread => !_hasUnread
    );

    export const allUnreadNotificationsCount: MxSelector<number> = createSelector(
      unread,
      (_unread) => _unread.length
    );

    export const allUnreadNotificationIds: MxSelector<number[]> = createSelector(
      unread,
      (_unread) => _unread.map(notification => notification.id)
    );

    // Selects the most recent notifications
    export const latestUnreadNotifications: MxSelector<Notification[]> = createSelector(
      unread,
      (_unread) => _unread.slice(0, AMOUNT_OF_VISIBLE_NOTIFICATIONS)
    );

    // Selects the total number of unread notifications, minus the most recent
    export const additionalNotificationsCount: MxSelector<number> = createSelector(
      unread,
      (_unread) => {
        const total = _unread.length;
        return (total > AMOUNT_OF_VISIBLE_NOTIFICATIONS) ? (total - AMOUNT_OF_VISIBLE_NOTIFICATIONS) : 0;
      }
    );

    export const hasAdditionalNotifications: MxSelector<boolean> = createSelector(
      additionalNotificationsCount,
      (_additionalCount) => isPositiveNumber(_additionalCount)
    );
  } // namespace Notifications
}
